import {
  Checkbox,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  SelectProps,
  SxProps,
  Theme,
  useTheme,
} from "@mui/material";
import { isNil } from "lodash-es";
import { ReactNode, useMemo } from "react";
import {
  Controller,
  FieldValues,
  RegisterOptions,
  UseControllerProps,
} from "react-hook-form";
import { ErrorDisplay } from "./ErrorDisplay";
import { TextWithTooltip } from "./TextWithTooltip";
import { useValidatingFormContext } from "hooks/useValidatingForm";
import { DisplayOnlyField } from "./DisplayOnlyField";

export type SelectItem = {
  value: any;
  display: string;
  disabled?: boolean;
};

export const FormMultiselect = <T extends FieldValues>({
  name,
  label,
  tooltip,
  disabled,
  control,
  selectProps,
  selectItems,
  helperText,
  rules,
  variant,
  sx,
  selectSx,
  hideErrors,
}: {
  label: string;
  tooltip?: ReactNode;
  disabled?: boolean;
  selectProps?: SelectProps;
  selectItems: SelectItem[];
  helperText?: string;
  rules?: RegisterOptions;
  variant?: "standard" | "outlined" | "filled";
  sx?: SxProps<Theme>;
  selectSx?: SxProps<Theme>;
  hideErrors?: boolean;
} & UseControllerProps<T>) => {
  const inputLabelId = useMemo(() => `${name}label`, [name]);
  const theme = useTheme();
  const labelWithTooltip = useMemo(
    () =>
      tooltip ? <TextWithTooltip text={label} tooltip={tooltip} /> : label,
    [label, tooltip]
  );

  const formContext = useValidatingFormContext();
  if (formContext?.metadata?.isDisplayOnly) {
    const value = formContext?.getValues(name) as string[] | string | undefined;
    const getDisplayText = () => {
      if (!selectItems?.length) return value?.toString() ?? "";

      if (Array.isArray(value)) {
        return value
          .map((v) => selectItems.find((item) => item.value === v)?.display)
          .filter(Boolean)
          .join(", ");
      }

      return selectItems.find((item) => item.value === value)?.display ?? "";
    };
    return (
      <DisplayOnlyField
        text={getDisplayText()}
        label={labelWithTooltip ?? ""}
      />
    );
  }

  const defaultValue = selectProps?.multiple ? [] : "";
  return (
    <FormControl fullWidth sx={sx} required={!!rules?.required}>
      <Controller
        name={name}
        control={control}
        rules={rules}
        render={({ field, fieldState: { error } }) => {
          let value: string | string[] = field.value;
          if (selectProps?.multiple) {
            value = Array.isArray(value) ? value : [];
          }
          if (isNil(value)) {
            value = defaultValue;
          }
          return (
            <>
              <InputLabel
                error={!!error}
                sx={{
                  color: disabled
                    ? theme.palette.action.disabled
                    : theme.palette.text.secondary,
                }}
                id={inputLabelId}
              >
                {labelWithTooltip}
              </InputLabel>
              <Select
                {...selectProps}
                {...field}
                value={value}
                renderValue={(selected) => {
                  if (selectProps?.multiple) {
                    if (Array.isArray(selected)) {
                      return selectItems
                        .filter((item) => selected.includes(item.value))
                        .map((item) => item.display)
                        .join(", ");
                    }
                    return "";
                  }
                  // Single select case
                  const selectedItem = selectItems.find(
                    (item) => item.value === selected
                  );
                  return selectedItem?.display || "";
                }}
                name={name}
                label={labelWithTooltip}
                error={!!error}
                labelId={inputLabelId}
                aria-labelledby={inputLabelId}
                disabled={disabled}
                variant={variant}
                sx={selectSx}
              >
                {selectItems?.map((item: SelectItem) => (
                  <MenuItem
                    key={item.value}
                    value={item.value}
                    disabled={item.disabled}
                  >
                    <Checkbox checked={value.includes(item.value)} />
                    <ListItemText primary={item.display} />
                  </MenuItem>
                ))}
              </Select>
              <ErrorDisplay
                error={error}
                helperText={helperText}
                hideErrors={hideErrors}
              />
            </>
          );
        }}
      />
    </FormControl>
  );
};
